import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useMutation, useApolloClient, ApolloClient, InMemoryCache, createHttpLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setpublisherGlobalData } from '../../redux/currentpublisher/publisherGlobalDataSlice.js';
import { makeInputs, makeDateString, checkValidity } from '../../util.js';
import CREATE_IMPRINT_MUTATION from "../../graphql/mutations/PublisherData/CreateImprint.js";
import CREATE_SERIES_MUTATION from "../../graphql/mutations/PublisherData/CreateSeries.js";
import CREATE_PRODUCT_WORK_AND_FORMAT_MUTATION from "../../graphql/mutations/Products/CreateProductWorkAndFormat.js";
import NewCreatorBox from "./NewCreatorBox.js";

import {
  TextInputFieldInput,
  TextInputWithUnitInput,
  TextareaFieldInput,
  ArrayInputFieldInput,
  DateFieldInput,
  CurrencyInput,
  ForeignKeyDropDownInput
} from "../../inputs/index.js";

import {
  Pane,
  Paragraph,
  Tablist,
  Tab,
  Button,
  toaster,
  Card
} from 'evergreen-ui';

function NewProductForm({ onClose, onSuccess }) {
  const navigate = useNavigate();
  const client = useApolloClient();
  const currentpublisher = useSelector((state) => state.currentpublisher.value);
  const publisherGlobalData = useSelector((state) => state.publisherGlobalData.value);
  const onixGlobalData = useSelector((state) => state.onixGlobalData.value);
  const dispatch = useDispatch();

  // Add console log to debug data
  console.log('NewProductForm loaded with:', {
    currentpublisher,
    publisherGlobalData,
    onixGlobalData,
    publisherImprints: publisherGlobalData?.publisherimprints || 'none',
    publisherSeries: publisherGlobalData?.productseriess || 'none',
    publisherCreators: publisherGlobalData?.publishercreators || 'none',
    productStatuses: onixGlobalData?.onixproductavailabilitys || 'none'
  });

  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    imprint: null,
    series: null,
    subtitle: '',
    status: null,
    volume: '',
    edition: '',
    isbn: '',
    retailPrice: '',
    inventoryQuantity: '',
    cartonQty: '',
    format: null,
    formatDetail: null,
    shipDate: null,
    publicationDate: null,
    weight: '',
    width: '',
    height: '',
    thickness: '',
    pageCount: '',
    adminNotes: '',
    audience: null,
    audienceDetails: '',
    keySellingPoints: '',
    marketing: '',
    bisac: [],
  });

  const [stagedCreators, setStagedCreators] = useState([]);

  const [tabs] = useState(['PRODUCT DETAILS', 'MARKETING'])
  const [selectedIndex, setSelectedIndex] = useState(0)

  const [createImprint] = useMutation(CREATE_IMPRINT_MUTATION);
  const [createSeries] = useMutation(CREATE_SERIES_MUTATION);
  const [createProductWorkAndFormat, { loading: savingProduct }] = useMutation(CREATE_PRODUCT_WORK_AND_FORMAT_MUTATION);

  // IMPORTANT: Define handleUpdateForm before any useEffect hooks that use it
  const handleUpdateForm = useCallback(({ key, val }) => {
    setFormData(prevData => {
      const currentValue = prevData[key];
      let changed = false;

      if (currentValue === null && val !== null) changed = true;
      else if (currentValue !== null && val === null) changed = true;
      else if (typeof currentValue === 'object' && currentValue !== null && typeof val === 'object' && val !== null) {
        changed = currentValue.id !== undefined && val.id !== undefined ? currentValue.id !== val.id : currentValue !== val;
      }
      else {
        changed = currentValue !== val;
      }

      if (changed) {
        return { ...prevData, [key]: val };
      } else {
        return prevData;
      }
    });
  }, []);

  // Define other handler functions that might be used in useEffect
  const handleAddNewImprint = useCallback(async (val) => {
     if (!currentpublisher?.id || !val) return;
    const imprints = [...(publisherGlobalData?.publisherimprints || [])];
    try {
        const token = localStorage.getItem('token');
        console.log('Imprint mutation - Auth Token:', token);

        const results = await createImprint({
            variables: { publisherId: String(currentpublisher.id), imprint: val },
            context: {
              headers: {
                Authorization: `Token ${token}`
              }
            }
        });
        const newImprintId = results.data?.createImprint?.imprint?.id;
        const newImprintValue = results.data?.createImprint?.imprint?.value;
        if (newImprintId && newImprintValue) {
            const newImprint = { id: parseInt(newImprintId), value: newImprintValue };
            dispatch(setpublisherGlobalData({ ...publisherGlobalData, publisherimprints: [...imprints, newImprint] }));
            handleUpdateForm({ key: 'imprint', val: newImprint });
            toaster.success(`Imprint "${val}" added.`);
        } else {
            toaster.warning("Failed to add imprint. Unexpected response.");
        }
    } catch (error) {
        toaster.danger(`Error adding imprint: ${error.message}`);
    }
  }, [dispatch, currentpublisher, publisherGlobalData, handleUpdateForm, createImprint]);

  const handleAddNewSeries = useCallback(async (val) => {
    if (!currentpublisher?.id || !val) return;
    const seriesList = [...(publisherGlobalData?.productseriess || [])];
     try {
        const results = await createSeries({
            variables: { publisherId: String(currentpublisher.id), series: val }
        });
        const newSeriesId = results.data?.createSeries?.series?.id;
        const newSeriesValue = results.data?.createSeries?.series?.value;
        if (newSeriesId && newSeriesValue) {
           const newSeries = { id: parseInt(newSeriesId), value: newSeriesValue };
            dispatch(setpublisherGlobalData({ ...publisherGlobalData, productseriess: [...seriesList, newSeries] }));
            handleUpdateForm({ key: 'series', val: newSeries });
            toaster.success(`Series "${val}" added.`);
        } else {
            toaster.warning("Failed to add series. Unexpected response.");
        }
    } catch (error) {
        toaster.danger(`Error adding series: ${error.message}`);
    }
  }, [dispatch, currentpublisher, publisherGlobalData, handleUpdateForm, createSeries]);

  const handleAttachCreator = useCallback((id, creator, code) => {
    if (!creator || !code || !creator.id || !code.id) {
      toaster.warning("Invalid creator or contributor code selected.");
      return;
    }

    let newCreatorEntry = {
      id: id ? id : -(stagedCreators.length + 1),
      creator: creator,
      onixContributorCode: code
    };

    setStagedCreators(prevCreators => {
      let updatedCreators = [...prevCreators];
      const existingIndex = updatedCreators.findIndex(c => c.id === newCreatorEntry.id);

      if (existingIndex > -1) {
        updatedCreators[existingIndex] = newCreatorEntry;
      } else {
        updatedCreators.push(newCreatorEntry);
      }
      return updatedCreators;
    });
  }, [stagedCreators.length]);

  const handleRemoveCreator = useCallback((creatorEntryId) => {
    setStagedCreators(prevCreators => prevCreators.filter(c => c.id !== creatorEntryId));
  }, []);

  // Now that the handler functions are defined, we can use them in useEffect hooks
  useEffect(() => {
    if (publisherGlobalData?.publisherimprints?.length > 0 && !formData.imprint) {
      const defaultImprint = publisherGlobalData.publisherimprints[0];
      if (defaultImprint && typeof defaultImprint === 'object' && defaultImprint.id !== undefined) {
         setFormData(prevData => ({ ...prevData, imprint: defaultImprint }));
      }
    }
  }, [publisherGlobalData?.publisherimprints]);

  useEffect(() => {
    setSaving(savingProduct);
  }, [savingProduct]);

  useEffect(() => {
    // Log the data we're working with to debug dropdown issues
    console.log('Publisher Global Data:', publisherGlobalData);
    console.log('Available imprints:', publisherGlobalData?.publisherimprints || []);
    console.log('Available series:', publisherGlobalData?.productseriess || []);
    console.log('Available creators:', publisherGlobalData?.publishercreators || []);
    console.log('Onix Global Data:', onixGlobalData);
    console.log('Product statuses:', onixGlobalData?.onixproductavailabilitys || []);

    // Set a default imprint if available
    if (publisherGlobalData?.publisherimprints && publisherGlobalData?.publisherimprints.length > 0 && !formData.imprint) {
      const defaultImprint = publisherGlobalData.publisherimprints[0];
      if (defaultImprint && typeof defaultImprint === 'object' && defaultImprint.id !== undefined) {
         handleUpdateForm({ key: 'imprint', val: defaultImprint });
         console.log('Set default imprint:', defaultImprint);
      }
    }

    // Set default status - prefer 'Active', fall back to first available
    if (onixGlobalData?.onixproductavailabilitys?.length > 0 && !formData.status) {
      // First try to find by description exact match to 'Active'
      let defaultStatus = onixGlobalData.onixproductavailabilitys.find(s =>
        s.description === 'Active' || s.value === 'Active'
      );

      // Then try partial match
      if (!defaultStatus) {
        defaultStatus = onixGlobalData.onixproductavailabilitys.find(s =>
          s.description?.toLowerCase().includes('active') ||
          s.value?.toLowerCase().includes('active')
        );
      }

      // Fall back to first available if no Active status found
      if (!defaultStatus && onixGlobalData.onixproductavailabilitys.length > 0) {
        defaultStatus = onixGlobalData.onixproductavailabilitys[0];
      }

      if (defaultStatus) {
        handleUpdateForm({ key: 'status', val: defaultStatus });
        console.log('Set status to:', defaultStatus);
      }
    }
  }, [publisherGlobalData, onixGlobalData, formData.imprint, formData.status, handleUpdateForm]);

  // Force load a default imprint if available but not loaded yet
  useEffect(() => {
    if (publisherGlobalData?.publisherimprints?.length > 0 && !formData.imprint) {
      setTimeout(() => {
        const defaultImprint = publisherGlobalData.publisherimprints[0];
        if (defaultImprint && typeof defaultImprint === 'object' && defaultImprint.id !== undefined) {
          console.log('Setting default imprint:', defaultImprint);
          handleUpdateForm({ key: 'imprint', val: defaultImprint });
        }
      }, 500); // Small delay to ensure state is ready
    }
  }, [publisherGlobalData?.publisherimprints, formData.imprint, handleUpdateForm]);

  // Add useMemo hook for form validation
  const isFormValid = useMemo(() => {
    const { title, format, retailPrice } = formData;
    const priceValid = retailPrice && !isNaN(parseFloat(retailPrice)) && parseFloat(retailPrice) > 0;
    const formatValid = format && format.id !== undefined && format.id !== null;
    const titleValid = title && title.trim() !== '';

    return titleValid && formatValid && priceValid;
  }, [formData]);

  const inputs = useMemo(() => ({
    title: { width: 350, comp: TextInputFieldInput, formLabel: "Title", handleChange: handleUpdateForm, group: 1, opts: { mandatory: true, marginRight: 16 } },
    subtitle: { width: 350, comp: TextInputFieldInput, formLabel: "Subtitle", handleChange: handleUpdateForm, group: 1, opts: { marginRight: 16 } },
    edition: { width: 75, comp: TextInputFieldInput, formLabel: "Edition", handleChange: handleUpdateForm, group: 11, opts: { marginRight: 16, marginBottom: 16 } },
    volume: { width: 75, comp: TextInputFieldInput, formLabel: "Volume", handleChange: handleUpdateForm, group: 11, opts: { marginRight: 16, marginBottom: 16 } },
    series: {
      width: 150,
      comp: ForeignKeyDropDownInput,
      formLabel: "Series",
      handleChange: handleUpdateForm,
      handleAddNew: handleAddNewSeries,
      group: 11,
      values: Array.isArray(publisherGlobalData?.productseriess) ? publisherGlobalData.productseriess : [],
      opts: { marginRight: 16, marginBottom: 16, new: true }
    },
    imprint: {
      width: 350,
      comp: ForeignKeyDropDownInput,
      formLabel: "Imprint",
      handleChange: handleUpdateForm,
      handleAddNew: handleAddNewImprint,
      group: 12,
      values: Array.isArray(publisherGlobalData?.publisherimprints) ? publisherGlobalData.publisherimprints : [],
      opts: { marginRight: 16, new: true }
    },
    description: { width: 350, comp: TextareaFieldInput, formLabel: "Description", handleChange: handleUpdateForm, group: 13, opts: { marginRight: 16 } },
    status: {
      width: 120,
      comp: ForeignKeyDropDownInput,
      formLabel: "STATUS:",
      handleChange: handleUpdateForm,
      group: 10,
      values: Array.isArray(onixGlobalData?.onixproductavailabilitys) ? onixGlobalData.onixproductavailabilitys : [],
      opts: { badge: true, marginRight: 16, short: true, preserveObject: true, valueKey: 'id', labelKey: 'description' }
    },
    format: {
      width: 120,
      comp: ForeignKeyDropDownInput,
      formLabel: "Format",
      handleChange: handleUpdateForm,
      group: 4,
      values: Array.isArray(onixGlobalData?.onixproductforms) ? onixGlobalData.onixproductforms : [],
      opts: { mandatory: true, marginRight: 16, short: true, preserveObject: true, valueKey: 'id', labelKey: 'description' }
    },
    formatDetail: {
      width: 120,
      comp: ForeignKeyDropDownInput,
      formLabel: "Format Detail",
      handleChange: handleUpdateForm,
      group: 41,
      values: Array.isArray(onixGlobalData?.onixproductformdetails) ? onixGlobalData.onixproductformdetails : [],
      opts: { marginRight: 16, short: true }
    },
    isbn: { width: 200, comp: TextInputFieldInput, formLabel: "ISBN/UID", handleChange: handleUpdateForm, group: 2, opts: { marginRight: 16 } },
    retailPrice: { width: 75, comp: CurrencyInput, formLabel: "Retail Price", handleChange: handleUpdateForm, group: 3, opts: { mandatory: true, marginRight: 16 } },
    inventoryQuantity: { width: 75, comp: TextInputFieldInput, formLabel: "Inventory", handleChange: handleUpdateForm, group: 3, opts: { marginRight: 16, marginBottom: 16 } },
    cartonQty: { width: 75, comp: TextInputFieldInput, formLabel: "Carton Qty", handleChange: handleUpdateForm, group: 3, opts: { marginRight: 16 } },
    shipDate: { width: 75, comp: DateFieldInput, formLabel: "Ship Date", handleChange: handleUpdateForm, group: 5, opts: { marginRight: 16, tooltip: "The date this title is released from the warehouse" } },
    publicationDate: { width: 75, comp: DateFieldInput, formLabel: "Publication Date", handleChange: handleUpdateForm, group: 5, opts: { marginRight: 16, tooltip: "The date this title is on sale in stores" } },
    weight: { width: 75, comp: TextInputWithUnitInput, formLabel: "Weight", handleChange: handleUpdateForm, group: 6, opts: { marginRight: 16 } },
    width: { width: 75, comp: TextInputWithUnitInput, formLabel: "Width", handleChange: handleUpdateForm, group: 6, opts: { marginRight: 16 } },
    height: { width: 75, comp: TextInputWithUnitInput, formLabel: "Height", handleChange: handleUpdateForm, group: 6, opts: { marginRight: 16 } },
    thickness: { width: 75, comp: TextInputWithUnitInput, formLabel: "Thickness", handleChange: handleUpdateForm, group: 61, opts: { marginRight: 16 } },
    pageCount: { width: 100, comp: TextInputFieldInput, formLabel: "Page Count", handleChange: handleUpdateForm, group: 61, opts: { marginRight: 16, marginBottom: 24 } },
    adminNotes: { width: 400, comp: TextareaFieldInput, formLabel: "Admin Notes(Only Visible Internally)", handleChange: handleUpdateForm, group: 7, opts: { marginRight: 16 } },
    audience: { width: 120, comp: ForeignKeyDropDownInput, formLabel: "Audience", handleChange: handleUpdateForm, group: 81, values: onixGlobalData?.onixaudiencetypes || [], opts: { marginRight: 16 } },
    audienceDetails: { width: 200, comp: TextInputFieldInput, formLabel: "Audience Details", handleChange: handleUpdateForm, group: 81, opts: { marginRight: 16 } },
    keySellingPoints: { width: 400, comp: TextareaFieldInput, formLabel: "Key Selling Points", handleChange: handleUpdateForm, group: 8, opts: { marginRight: 16 } },
    marketing: { width: 400, comp: TextareaFieldInput, formLabel: "Marketing Notes", handleChange: handleUpdateForm, group: 8, opts: { marginRight: 16 } },
    bisac: { width: 75, comp: ArrayInputFieldInput, formLabel: "BISAC", handleChange: handleUpdateForm, group: 82, max: 4 },
  }), [handleUpdateForm, handleAddNewImprint, handleAddNewSeries, publisherGlobalData, onixGlobalData]);

  const formInputs = makeInputs(formData, inputs, true);

  const handleSave = async () => {
    setSaving(true);
    const validationFields = [
      { name: "title", required: true },
      { name: "format", required: true, fieldType: "object", field: "id" },
      { name: "retailPrice", required: true, fieldType: "number" },
    ];

    let isValid = checkValidity(validationFields, formData);

    if (isValid && (!formData.retailPrice || isNaN(parseFloat(formData.retailPrice)) || parseFloat(formData.retailPrice) <= 0)) {
        isValid = false;
        toaster.danger("Retail Price must be a valid positive number.");
    }

    if (!isValid) {
       toaster.danger("Please fill in all required fields and correct any errors (Title, Format, Retail Price).");
       setSaving(false);
       return;
    }

    // Parse values from the form
    const validatedRetailPrice = parseFloat(formData.retailPrice);
    const validatedInventory = formData.inventoryQuantity ? parseInt(formData.inventoryQuantity) : null;
    const validatedCartonQty = formData.cartonQty ? parseInt(formData.cartonQty) : null;
    const validatedPageCount = formData.pageCount ? parseInt(formData.pageCount) : null;

    // Ensure dimensions are properly converted for backend storage
    // Weight and dimensions need to be simple numeric values
    const validatedWeight = formData.weight && formData.weight.value ? parseFloat(formData.weight.value) :
                           (formData.weight ? parseFloat(formData.weight) : null);
    const validatedWidth = formData.width && formData.width.value ? parseFloat(formData.width.value) :
                          (formData.width ? parseFloat(formData.width) : null);
    const validatedHeight = formData.height && formData.height.value ? parseFloat(formData.height.value) :
                           (formData.height ? parseFloat(formData.height) : null);
    const validatedThickness = formData.thickness && formData.thickness.value ? parseFloat(formData.thickness.value) :
                              (formData.thickness ? parseFloat(formData.thickness) : null);

    // Ensure dates are properly formatted
    const shipDate = formData.shipDate ? makeDateString(formData.shipDate) : null;
    const publicationDate = formData.publicationDate ? makeDateString(formData.publicationDate) : null;

    const productWorkInput = {
        publisherId: String(currentpublisher.id),
        title: formData.title,
        description: formData.description || null,
        imprintId: formData.imprint?.id ? String(formData.imprint.id) : null,
        seriesId: formData.series?.id ? String(formData.series.id) : null,
    };

    const productInput = {
        subtitle: formData.subtitle || null,
        statusId: formData.status?.id ? String(formData.status.id) : null,
        formatId: String(formData.format.id),
        formatDetailId: formData.formatDetail?.id ? String(formData.formatDetail.id) : null,
        isbn: formData.isbn || null,
        retailPrice: validatedRetailPrice,
        inventoryQuantity: validatedInventory,
        cartonQty: validatedCartonQty,
        shipDate: shipDate,
        publicationDate: publicationDate,
        weight: validatedWeight,
        width: validatedWidth,
        height: validatedHeight,
        thickness: validatedThickness,
        pageCount: validatedPageCount,
        adminNotes: formData.adminNotes || null,
        audienceId: formData.audience?.id ? String(formData.audience.id) : null,
        audienceDetails: formData.audienceDetails || null,
        keySellingPoints: formData.keySellingPoints || null,
        marketing: formData.marketing || null,
        bisac: formData.bisac?.length > 0 ? JSON.stringify(formData.bisac) : null,
        edition: formData.edition ? parseInt(formData.edition) : null,
        volume: formData.volume ? parseInt(formData.volume) : null,
    };

    const creatorsInput = stagedCreators.map(sc => ({
        creatorId: String(sc.creator.id),
        onixContributorCodeId: String(sc.onixContributorCode.id),
    }));

    console.log("Submitting Data:", { productWorkInput, productInput, creatorsInput });
    console.log("Staged Creators:", stagedCreators);
    console.log("Creators Input Length:", creatorsInput.length);
    toaster.notify("Saving new product...", { duration: 3 });

    try {
        const token = localStorage.getItem('token');
        console.log('Auth Token for mutation:', token);

        // Test if token exists and has proper format
        if (!token) {
          console.error('No token found in localStorage');
          toaster.danger("Authentication error: No token found");
          setSaving(false);
          return;
        }

        // Create a special Apollo client instance just for this request
        // with the same setup as the main client
        const httpLink = createHttpLink({
          uri: `/graphql-auth/`,
          credentials: 'include',
        });

        // Create auth link that explicitly adds the token
        const authLink = setContext((_, { headers }) => {
          return {
            headers: {
              ...headers,
              authorization: `Token ${token}`,
            }
          };
        });

        // Create a new Apollo client with this configuration
        const specialClient = new ApolloClient({
          link: authLink.concat(httpLink),
          cache: new InMemoryCache(),
          defaultOptions: {
            mutate: {
              fetchPolicy: 'no-cache',
            },
          },
        });

        // Build variables object explicitly
        const mutationVariables = {
            productWorkInput,
            productInput,
        };
        
        // Only add creatorsInput if there are creators
        if (creatorsInput.length > 0) {
            mutationVariables.creatorsInput = creatorsInput;
        }
        
        console.log("Final mutation variables:", mutationVariables);

        // Use the new client for this specific mutation
        const response = await specialClient.mutate({
          mutation: CREATE_PRODUCT_WORK_AND_FORMAT_MUTATION,
          variables: mutationVariables,
        });

        console.log('Special client response:', response);
        const saveData = response.data;

        console.log("Save Response:", saveData);

        if (saveData?.createProductWorkAndFormat?.ok) {
            const newProductWorkId = saveData.createProductWorkAndFormat.productWork?.id;
            const newProductId = saveData.createProductWorkAndFormat.productWork?.products?.[0]?.id;
            toaster.success("Product created successfully!");

            if (onSuccess) {
              // Call success callback if provided
              onSuccess();
            } else if (newProductId) {
              // Fall back to navigation if no callback
              navigate(`/products/${newProductId}`);
            } else {
              navigate('/products');
            }
        } else {
            const errorMessage = saveData?.createProductWorkAndFormat?.message || "Failed to save product. Unknown error.";
            toaster.danger(errorMessage);
            setSaving(false);
        }
    } catch (error) {
        console.error("Error saving product:", error);
        toaster.danger(`Failed to save product: ${error.message}`);
        setSaving(false);
    }
  };

  return (
    <Pane padding={16} background="white" height="100%">
      <Pane display="flex" justifyContent="space-between" alignItems="center" marginBottom={16}>
         <Paragraph size={600} fontWeight="bold">Create New Product</Paragraph>
         <Pane>
             {/* Use a single Button with primary appearance for Save */}
             <Button
               appearance="confirm-button"
               intent="success"
               marginRight={12}
               onClick={handleSave}
               isLoading={saving}
               disabled={saving || !isFormValid}
               className="btn-save-cancel-txt-bold"
             >
               {saving ? 'Saving...' : 'Save Product'}
             </Button>
             <Button onClick={onClose || (() => navigate('/products'))} disabled={saving}>
               Cancel
             </Button>
         </Pane>
      </Pane>

      <Card elevation={1} background="white" padding={16}>
        <Tablist marginBottom={16}>
          {tabs.map((tab, index) => (
            <Tab
              aria-controls={`panel-${tab}`}
              isSelected={index === selectedIndex}
              key={tab}
              onSelect={() => setSelectedIndex(index)}
              className="custom-tab"
              fontWeight="bold"
            >
              {tab}
            </Tab>
          ))}
        </Tablist>

        <Pane
            id="panel-PRODUCT DETAILS"
            role="tabpanel"
            aria-labelledby="PRODUCT DETAILS"
            aria-hidden={selectedIndex !== 0}
            display={selectedIndex === 0 ? 'flex' : 'none'}
            flexWrap="nowrap"
            overflowX="auto"
          >
            <Pane display="flex" flexDirection="column" borderRight="default" paddingRight={16} marginRight={16} flexBasis="auto" flexShrink={0} justifyContent="flex-start" alignItems="flex-start" marginBottom={16}>
               {formInputs.group10}
               {formInputs.group1}
               <Pane display="flex">
                 {formInputs.group11}
               </Pane>
               {formInputs.group12}
               {formInputs.group13}
            </Pane>
            <Pane display="flex" flexDirection="column" paddingX={16} borderRight="default" marginRight={16} flexBasis="auto" marginBottom={16}>
               <Pane display="flex" flexDirection="row" justifyContent="flex-start" alignItems="flex-start">
                  {formInputs.group4}
               </Pane>
               {formData.format?.description === "Hardcover" && formInputs.group41}
               <Pane display="flex" flexDirection="row" justifyContent="flex-start" alignItems="flex-start">
                  {formInputs.group2}
               </Pane>
               <Pane display="flex" flexDirection="row" justifyContent="flex-start" alignItems="flex-start">
                  {formInputs.group3}
               </Pane>
               <Pane display="flex" flexDirection="row" justifyContent="flex-start" alignItems="flex-start">
                  {formInputs.group5}
               </Pane>
               <Pane display="flex" flexDirection="row" justifyContent="flex-start" alignItems="flex-start">
                  {formInputs.group6}
               </Pane>
               <Pane display="flex" flexDirection="row" justifyContent="flex-start" alignItems="flex-start">
                  {formInputs.group61}
               </Pane>
            </Pane>
            <Pane display="flex" flexDirection="column" paddingLeft={12} flexBasis="auto" justifyContent="flex-start" alignItems="flex-start" marginBottom={16}>
               {formInputs.group7}
               <Pane marginTop={16}>
                  <NewCreatorBox
                   editing={true}
                   onixGlobalData={onixGlobalData || {}}
                   royaltySet={[]}
                   productcreatorSet={stagedCreators}
                   handleUpdateCreatorStaged={handleAttachCreator}
                   creators={Array.isArray(publisherGlobalData?.publishercreators) ? publisherGlobalData.publishercreators : []}
                   handleDelete={handleRemoveCreator}
                   productId={null}
                   editMode="new"
                 />
               </Pane>
            </Pane>
        </Pane>

        <Pane
            id="panel-MARKETING"
            role="tabpanel"
            aria-labelledby="MARKETING"
            aria-hidden={selectedIndex !== 1}
            display={selectedIndex === 1 ? 'flex' : 'none'}
            flexWrap="nowrap"
            overflowX="auto"
        >
             <Pane display="flex" flexDirection="column" paddingX={16} borderRight="default" marginRight={16} flex={3} flexShrink={0} marginBottom={16}>
                <Pane display="flex">{formInputs.group81}</Pane>
                {formInputs.group82}
                {formInputs.group8}
             </Pane>
        </Pane>
      </Card>

    </Pane>
  )
}

export default NewProductForm;
